import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailPlus, MailX, RotateCcw, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * DEV-011C · Operations Customer Invite panel.
 *
 * Compact controls to send / resend / revoke a customer activation
 * invitation from within the Operations customer detail workspace.
 * Never displays raw tokens or DB identifiers; always shows a safe
 * status chip and the effective expiry timestamp.
 */
const STATE_LABELS = {
    not_invited:     { label: "Not invited",   tone: "muted" },
    invite_pending:  { label: "Invite pending", tone: "brand" },
    pending:         { label: "Invite pending", tone: "brand" },
    active:          { label: "Active",         tone: "success" },
    expired:         { label: "Invite expired", tone: "warning" },
    revoked:         { label: "Invite revoked", tone: "warning" },
    used:            { label: "Activated",      tone: "success" },
};

const TONE_CLASSES = {
    muted:   "bg-[#F5F5F7] text-[#4B5565] border-[#E5E7EB]",
    brand:   "bg-[#E7F3E1] text-[#2E7A22] border-[#C9E7BE]",
    success: "bg-[#E7F3E1] text-[#2E7A22] border-[#C9E7BE]",
    warning: "bg-[#FFF3E0] text-[#B45309] border-[#FDE68A]",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function StatusChip({ state }) {
    const meta = STATE_LABELS[state] || STATE_LABELS.not_invited;
    return (
        <span
            data-testid={`invite-status-${state}`}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${TONE_CLASSES[meta.tone]}`}
        >
            {meta.label}
        </span>
    );
}

export default function CustomerInvitePanel({ customer, projects = [] }) {
    const customerId = customer?.id;
    const defaultProjectId = projects[0]?.id || null;
    const [projectId, setProjectId] = useState(defaultProjectId);
    const [state, setState] = useState("not_invited");
    const [invitations, setInvitations] = useState([]);
    const [eligibility, setEligibility] = useState({ eligible: true, reason: null, message: "" });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const resp = await api.opsListInvitations(customerId);
            setState(resp.account_state || "not_invited");
            setInvitations(resp.invitations || []);
            // DEV-011C1 · Commercial-eligibility envelope. Fall back to
            // eligible=true only when the field is missing (older API
            // shape) so the backend gate — which is authoritative —
            // stays the sole enforcement point.
            setEligibility(
                resp.eligibility ?? { eligible: true, reason: null, message: "" },
            );
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load invitations");
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setProjectId(defaultProjectId); }, [defaultProjectId]);

    const latest = invitations[0] || null;
    const canSend = state === "not_invited";
    const canResend = ["invite_pending", "pending", "expired", "revoked"].includes(state);
    const canRevoke = ["invite_pending", "pending"].includes(state) && latest;
    // DEV-011C1 · The invitation action is only available while the
    // customer is commercially committed. Active accounts and revoke
    // are unaffected. Frontend disabling is a UX affordance; the
    // backend remains the source of truth.
    const inviteBlocked = !eligibility.eligible && state !== "active";
    const eligibilityMessage = eligibility.message || "The contract must be signed before you can invite this customer.";

    const sendOrResend = useCallback(async (kind) => {
        if (!projectId) { toast.error("Select a project to link the invitation to."); return; }
        setBusy(true);
        try {
            const fn = kind === "resend" ? api.opsResendInvitation : api.opsSendInvitation;
            const resp = await fn(customerId, { project_id: projectId });
            const delivery = resp?.delivery;
            if (delivery === "suppressed") {
                toast.success("Invitation queued — outbound email is currently suppressed.");
            } else {
                toast.success(kind === "resend" ? "Invitation resent." : "Invitation sent.");
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to send invitation");
        } finally {
            setBusy(false);
        }
    }, [customerId, projectId, load]);

    const revoke = useCallback(async () => {
        if (!latest?.id) return;
        setBusy(true);
        try {
            await api.opsRevokeInvitation(customerId, latest.id);
            toast.success("Invitation revoked.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to revoke invitation");
        } finally {
            setBusy(false);
        }
    }, [customerId, latest, load]);

    if (!customerId) return null;

    return (
        <section className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5" data-testid="ops-invite-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#4B5565]">Customer account</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <ShieldCheck size={14} className="text-[#3E9E2E]" />
                        <StatusChip state={state} />
                        {loading && <Loader2 className="animate-spin" size={14} />}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {projects.length > 1 && (canSend || canResend) && (
                        <select
                            className="pe-input h-8 text-sm"
                            value={projectId || ""}
                            onChange={(e) => setProjectId(e.target.value)}
                            data-testid="ops-invite-project"
                        >
                            {projects.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.reference || p.address || p.id}
                                </option>
                            ))}
                        </select>
                    )}
                    {canSend && (
                        <button
                            onClick={() => sendOrResend("send")}
                            disabled={busy || !projectId || inviteBlocked}
                            title={inviteBlocked ? eligibilityMessage : undefined}
                            className="pe-btn-primary text-sm h-9"
                            data-testid="ops-invite-send"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <MailPlus size={14} />}
                            Send invitation
                        </button>
                    )}
                    {canResend && (
                        <button
                            onClick={() => sendOrResend("resend")}
                            disabled={busy || !projectId || inviteBlocked}
                            title={inviteBlocked ? eligibilityMessage : undefined}
                            className="pe-btn-primary text-sm h-9"
                            data-testid="ops-invite-resend"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                            Resend invitation
                        </button>
                    )}
                    {canRevoke && (
                        <button
                            onClick={revoke}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-[#B91C1C] text-sm hover:bg-[#FEF2F2]"
                            data-testid="ops-invite-revoke"
                        >
                            <MailX size={14} /> Revoke
                        </button>
                    )}
                </div>
            </div>

            {inviteBlocked && (canSend || canResend) && (
                <div
                    className="mt-3 p-3 rounded-md bg-[#FFF7ED] border border-[#FED7AA] text-[12px] text-[#B45309] inline-flex items-start gap-2"
                    data-testid="ops-invite-eligibility-blocker"
                    role="status"
                >
                    <AlertCircle size={14} className="mt-[1px] shrink-0" />
                    <span>{eligibilityMessage}</span>
                </div>
            )}

            {invitations.length > 0 && (
                <div className="mt-4 border-t border-[#EDEFF3] pt-3">
                    <div className="text-[10px] uppercase tracking-wide text-[#4B5565] mb-2">Recent invitations</div>
                    <ul className="text-xs text-[#131B2F] space-y-1.5">
                        {invitations.slice(0, 4).map((inv) => (
                            <li key={inv.id} className="flex items-center gap-2 flex-wrap" data-testid={`invite-row-${inv.id}`}>
                                <StatusChip state={inv.status} />
                                <span className="text-[#4B5565]">
                                    {inv.customer_email} · sent {formatDate(inv.created_at)}
                                    {inv.status === "pending" && ` · expires ${formatDate(inv.expires_at)}`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {state === "active" && (
                <div className="mt-3 text-[11px] text-[#2E7A22] inline-flex items-center gap-1.5" data-testid="ops-invite-active">
                    <CheckCircle2 size={12} /> The customer has an active account and can sign in.
                </div>
            )}
            {state === "not_invited" && invitations.length === 0 && (
                <div className="mt-3 text-[11px] text-[#4B5565] inline-flex items-center gap-1.5">
                    <AlertCircle size={12} /> No invitation has been sent for this customer yet.
                </div>
            )}
        </section>
    );
}
